import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Target, Trophy, DollarSign, Plus, Loader2, Check, Trash2, Flag,
  TrendingUp, CheckCircle2, Circle, Minus, Flame, Banknote, Sparkles, MapPin, Award, AlertTriangle,
} from "lucide-react";

const TYPES = {
  earnings: { label: "Season Earnings", icon: DollarSign, color: "#3B6B1E" },
  cfr: { label: "Make the CFR (Top 12)", icon: Trophy, color: "#B8860B" },
  custom: { label: "Custom Goal", icon: Flag, color: "#C8000F" },
};

function pct(n) { return `${Math.max(0, Math.min(100, Math.round(n)))}%`; }

const STATUS = {
  hit: { label: "Hit", cls: "bg-[#3B6B1E]/15 text-[#3B6B1E]", icon: CheckCircle2 },
  on_track: { label: "On track", cls: "bg-[#3B6B1E]/12 text-[#3B6B1E]", icon: TrendingUp },
  behind: { label: "Behind", cls: "bg-[#C8000F]/12 text-[#C8000F]", icon: AlertTriangle },
  pending: { label: "Tracking", cls: "bg-slate-400/15 text-slate-500", icon: Circle },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  const Icon = s.icon;
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md ${s.cls}`} data-testid={`goal-status-${status}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function GoalCard({ m, goal, onPatch, onDelete }) {
  const t = TYPES[goal.type] || TYPES.custom;
  const Icon = t.icon;
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const achieved = goal.status === "hit";
  const progress = goal.progress_pct || 0;
  const barColor = achieved ? "#3B6B1E" : goal.status === "behind" ? "#C8000F" : t.color;
  const showBar = goal.type !== "custom" || goal.target;
  const manual = goal.type === "custom" && !goal.auto;
  const ring = achieved ? "ring-1 ring-[#3B6B1E]/40" : goal.status === "behind" ? "ring-1 ring-[#C8000F]/30" : "";

  return (
    <div className={`rounded-xl border p-4 ${card} ${ring}`} data-testid={`goal-card-${goal.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${t.color}1A`, color: t.color }}><Icon className="w-4.5 h-4.5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-display font-bold truncate ${title}`}>{goal.title}</span>
            <StatusBadge status={goal.status} />
          </div>
          <div className="text-xs text-slate-400 mt-0.5">{goal.detail}</div>
          {showBar && (
            <div className={`h-2 rounded-full mt-2 overflow-hidden ${m ? "bg-white/10" : "bg-[#EEF0F2]"}`}>
              <div className="h-full rounded-full transition-all" style={{ width: pct(progress), background: barColor }} />
            </div>
          )}
          {goal.deadline && <div className="text-[11px] text-slate-400 mt-1.5">Target date: {new Date(goal.deadline).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>}
          {manual && (
            <div className="flex items-center gap-2 mt-2">
              {goal.target ? (
                <div className="flex items-center gap-1">
                  <button onClick={() => onPatch(goal.id, { current: Math.max(0, (goal.current || 0) - 1) })} data-testid={`goal-dec-${goal.id}`} className="w-6 h-6 rounded bg-[#F1F3F5] text-slate-600 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="text-sm font-bold w-8 text-center" style={{ color: t.color }}>{goal.current || 0}</span>
                  <button onClick={() => onPatch(goal.id, { current: (goal.current || 0) + 1 })} data-testid={`goal-inc-${goal.id}`} className="w-6 h-6 rounded bg-[#F1F3F5] text-slate-600 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button onClick={() => onPatch(goal.id, { done: !goal.done })} data-testid={`goal-toggle-${goal.id}`} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500">
                  {goal.done ? <CheckCircle2 className="w-4 h-4 text-[#3B6B1E]" /> : <Circle className="w-4 h-4" />} {goal.done ? "Done" : "Mark done"}
                </button>
              )}
            </div>
          )}
        </div>
        <button onClick={() => onDelete(goal.id)} data-testid={`goal-delete-${goal.id}`} className="text-slate-300 hover:text-[#C8000F] shrink-0"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function EarningsTrajectory({ m, data }) {
  const { trajectory = [], season = {}, earnings_target: target, total_earnings: current = 0 } = data || {};
  if (!target || !season) return null;
  const startMs = Date.parse(season.start), endMs = Date.parse(season.end);
  const spanOk = isFinite(startMs) && isFinite(endMs) && endMs > startMs;
  const elapsed = Math.max(0.02, Math.min(1, (season.elapsed_pct || 0) / 100));
  const n = trajectory.length;
  const fracOf = (d, i) => {
    const dd = Date.parse(d);
    if (spanOk && isFinite(dd)) return Math.max(0, Math.min(1, (dd - startMs) / (endMs - startMs)));
    return n > 1 ? (i / (n - 1)) * elapsed : elapsed;
  };
  const ymax = (Math.max(target, current) * 1.08) || 1;
  const pts = [{ x: 0, y: 0 }];
  trajectory.forEach((p, i) => pts.push({ x: fracOf(p.date, i), y: p.earnings }));
  pts.push({ x: elapsed, y: current });
  pts.sort((a, b) => a.x - b.x);
  const X = (x) => (x * 100).toFixed(2);
  const Y = (y) => (40 - (y / ymax) * 40).toFixed(2);
  const actual = pts.map((p) => `${X(p.x)},${Y(p.y)}`).join(" ");
  const area = `0,40 ${actual} ${X(pts[pts.length - 1].x)},40`;
  const targetY = Y(target);
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const ahead = current >= target * elapsed;

  return (
    <div className={`rounded-xl border p-4 ${card}`} data-testid="earnings-trajectory">
      <div className="flex items-center justify-between mb-1">
        <span className={`font-display font-bold text-sm ${title}`}>Earnings pace</span>
        <span className={`text-[11px] font-bold ${ahead ? "text-[#3B6B1E]" : "text-[#C8000F]"}`}>{ahead ? "Ahead of pace" : "Behind pace"}</span>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-display font-bold text-[#3B6B1E]">{money(current)}</span>
        <span className="text-xs text-slate-400">of {money(target)} target</span>
      </div>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-24">
        <defs>
          <linearGradient id="earnFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B6B1E" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#3B6B1E" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1="40" x2="100" y2={targetY} stroke="#B8860B" strokeWidth="1" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
        <polygon points={area} fill="url(#earnFill)" />
        <polyline points={actual} fill="none" stroke="#3B6B1E" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <line x1={X(elapsed)} y1="0" x2={X(elapsed)} y2="40" stroke={m ? "#334155" : "#CBD5E1"} strokeWidth="1" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1.5">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-[#3B6B1E]" /> Your earnings</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 border-t border-dashed border-[#B8860B]" /> Target pace</span>
        <span>{season.rodeos_remaining != null ? `${season.rodeos_remaining} rodeos left` : ""}</span>
      </div>
    </div>
  );
}

function AutoCfrCard({ m, ev }) {
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const progress = ev.in_top12 ? 100 : (ev.cfr_cutoff ? (ev.earnings / ev.cfr_cutoff) * 100 : 0);
  return (
    <div className={`rounded-xl border p-4 ${card} ${ev.in_top12 ? "ring-1 ring-[#3B6B1E]/40" : ""}`} data-testid={`cfr-card-${ev.event}`}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#B8860B]/15 text-[#B8860B]"><Trophy className="w-4.5 h-4.5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-display font-bold truncate ${title}`}>CFR — {ev.event}</span>
            {ev.in_top12 && <CheckCircle2 className="w-4 h-4 text-[#3B6B1E] shrink-0" />}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {ev.in_top12
              ? <span className="text-[#3B6B1E] font-semibold">Qualifying — #{ev.rank}{ev.cushion != null ? ` · ${money(ev.cushion)} cushion` : ""}</span>
              : <>Currently #{ev.rank} · {money(ev.gap_to_top12)} back of top 12</>}
          </div>
          <div className={`h-2 rounded-full mt-2 overflow-hidden ${m ? "bg-white/10" : "bg-[#EEF0F2]"}`}>
            <div className="h-full rounded-full transition-all" style={{ width: pct(progress), background: ev.in_top12 ? "#3B6B1E" : "#B8860B" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MilestoneStat({ m, icon: Icon, label, value, color }) {
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  return (
    <div className={`rounded-xl border p-3 flex items-center gap-3 ${card}`} data-testid={`milestone-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1A`, color }}><Icon className="w-4.5 h-4.5" /></span>
      <div className="min-w-0">
        <div className={`font-display font-bold text-lg leading-none ${title}`}>{value}</div>
        <div className="text-[11px] text-slate-400 mt-1">{label}</div>
      </div>
    </div>
  );
}

function CoachSuggestDialog({ suggestion, onClose, onAdd, m }) {
  const DLG = m ? "bg-[#12161C] text-slate-100 border-white/10" : "bg-white text-[#14181F] border-[#E4E7EB]";
  const [added, setAdded] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAdded({}); }, [suggestion]);
  const goals = suggestion?.goals || [];
  const bodyOf = (g) =>
    g.type === "earnings" ? { type: "earnings", title: g.title || "Coach's earnings target", target: g.target }
      : g.type === "cfr" ? { type: "cfr", title: g.title, event: g.event }
        : { type: "custom", title: g.title, target: g.target || undefined, unit: g.unit || undefined };
  const addOne = async (g, i) => {
    setBusy(true);
    const r = await onAdd(bodyOf(g));
    if (r) setAdded((a) => ({ ...a, [i]: true }));
    setBusy(false);
  };
  const addAll = async () => {
    setBusy(true);
    let n = 0;
    for (let i = 0; i < goals.length; i++) {
      if (added[i]) continue;
      const r = await onAdd(bodyOf(goals[i]), { silent: true });
      if (r) { n++; setAdded((a) => ({ ...a, [i]: true })); }
    }
    setBusy(false);
    if (n) toast.success(`${n} goal${n === 1 ? "" : "s"} added`);
    onClose();
  };
  const allAdded = goals.length > 0 && goals.every((_, i) => added[i]);
  return (
    <Dialog open={!!suggestion} onOpenChange={onClose}>
      <DialogContent className={`${DLG} max-h-[85vh] overflow-y-auto`} data-testid="coach-suggest-dialog">
        {suggestion && (
          <>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C8000F]" /> Coach's suggested goals</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-400 -mt-1">Based on your real standings — you're at {money(suggestion.current_earnings || 0)} now. Add the ones you want.</p>
            <div className="space-y-2.5">
              {goals.map((g, i) => {
                const t = TYPES[g.type] || TYPES.custom;
                const Icon = t.icon;
                return (
                  <div key={i} className={`rounded-xl border p-3 ${m ? "bg-white/5 border-white/10" : "bg-[#F8FAFC] border-[#E4E7EB]"}`} data-testid={`coach-goal-${i}`}>
                    <div className="flex items-start gap-2.5">
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${t.color}1A`, color: t.color }}><Icon className="w-4 h-4" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm leading-tight">{g.title}</div>
                        {g.type === "earnings" && g.target > 0 && <div className="text-lg font-display font-bold text-[#3B6B1E] mt-0.5">{money(g.target)}</div>}
                        {g.type === "custom" && g.target > 0 && <div className="text-sm font-bold mt-0.5" style={{ color: t.color }}>{g.target}{g.unit ? ` ${g.unit}` : ""}</div>}
                        {g.rationale && <p className="text-xs text-slate-500 leading-relaxed mt-1">{g.rationale}</p>}
                        {g.rodeos?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {g.rodeos.map((r, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-md bg-[#C8000F]/10 text-[#C8000F] flex items-center gap-1"><MapPin className="w-3 h-3" />{r}</span>)}
                          </div>
                        )}
                      </div>
                      <button onClick={() => addOne(g, i)} disabled={busy || added[i]} data-testid={`coach-add-${i}`}
                        className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 ${added[i] ? "bg-[#3B6B1E]/15 text-[#3B6B1E]" : "bg-[#C8000F] hover:bg-[#9E0008] text-white"} disabled:opacity-60`}>
                        {added[i] ? <><Check className="w-3.5 h-3.5" /> Added</> : "Add"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={addAll} disabled={busy || allAdded} data-testid="coach-add-all-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg mt-1 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {allAdded ? "All added" : "Add all goals"}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Goals() {
  const m = useIsMobile();
  const nav = useNavigate();
  const [tracker, setTracker] = useState(null);
  const [plan, setPlan] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [adding, setAdding] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);

  const refetch = () => api.get("/me/goals/tracker").then((r) => setTracker(r.data)).catch(() => {});
  const load = useCallback(() => {
    api.get("/me/goals/tracker").then((r) => setTracker(r.data)).catch(() => setTracker({ goals: [] }));
    api.get("/me/season-plan").then((r) => setPlan(r.data)).catch(() => setPlan({}));
    api.get("/me/milestones").then((r) => setMilestones(r.data)).catch(() => setMilestones({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try { await api.patch(`/me/goals/${id}`, body); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/me/goals/${id}`); refetch(); toast.success("Goal removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const add = async (body, opts = {}) => {
    try { const { data } = await api.post("/me/goals", body); refetch(); if (!opts.silent) { setAdding(false); toast.success("Goal added"); } return data; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const askCoach = async () => {
    setSuggesting(true);
    try { const { data } = await api.post("/me/goals/suggest"); setSuggestion(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };
  const saveName = async () => {
    if (!nameInput.trim()) return;
    setSavingName(true);
    try { await api.post("/me/competitor", { name: nameInput.trim() }); toast.success("Name saved — pulling your standings"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingName(false); }
  };

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const hasName = plan && plan.competitor_name;
  const cfrEvents = (plan?.events || []);
  const goals = tracker?.goals ?? null;
  const summary = tracker?.summary;
  const achieved = summary?.hit || 0;

  return (
    <div className={page} data-testid="goals-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center justify-between gap-3 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <div className="flex items-center gap-2">
          <Target className="w-6 h-6 text-[#C8000F]" />
          <div>
            <h1 className={`font-display font-bold text-xl ${title}`}>Goals</h1>
            <p className="text-xs text-slate-400">{plan?.competitor_name ? `${plan.competitor_name} · ` : ""}{(goals || []).length} goal{(goals || []).length === 1 ? "" : "s"}{achieved ? ` · ${achieved} hit` : ""}</p>
          </div>
        </div>
        <button onClick={() => setAdding(true)} data-testid="goal-add-btn" className="flex items-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg">
          <Plus className="w-4 h-4" /> New goal
        </button>
      </div>

      <div className="px-4 sm:px-6 py-5 max-w-2xl space-y-3">
        {/* Set your name to unlock auto-tracking */}
        {plan && !hasName && (
          <div className={`rounded-xl border p-4 ${card}`} data-testid="goal-name-setter">
            <div className={`font-display font-bold ${title}`}>Track your real standings</div>
            <p className="text-sm text-slate-400 mt-0.5 mb-2.5">Enter your competitor name exactly as it appears in CPRA standings to auto-track earnings, your CFR race and milestones.</p>
            <div className="flex gap-2">
              <input value={nameInput} onChange={(e) => setNameInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName()} placeholder="e.g. Zeke Thurston" data-testid="goal-name-input"
                className={`flex-1 rounded-lg border px-3 py-2.5 text-sm ${m ? "bg-white/5 border-white/10 text-slate-100" : "bg-white border-[#D7DBE0] text-[#14181F]"}`} />
              <button onClick={saveName} disabled={savingName} data-testid="goal-name-save" className="bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 rounded-lg disabled:opacity-50">{savingName ? "…" : "Save"}</button>
            </div>
          </div>
        )}

        {/* Goals scoreboard summary */}
        {hasName && summary && summary.total > 0 && (
          <div className={`rounded-xl border p-3 flex flex-wrap items-center gap-2 ${card}`} data-testid="goals-summary">
            <span className={`font-display font-bold text-sm ${title}`}>{summary.on_track + summary.hit} of {summary.total} on track</span>
            {summary.hit > 0 && <span className="text-[11px] font-bold px-2 py-1 rounded-md bg-[#3B6B1E]/15 text-[#3B6B1E]" data-testid="summary-hit">{summary.hit} hit</span>}
            {summary.on_track > 0 && <span className="text-[11px] font-bold px-2 py-1 rounded-md bg-[#3B6B1E]/10 text-[#3B6B1E]" data-testid="summary-ontrack">{summary.on_track} on track</span>}
            {summary.behind > 0 && <span className="text-[11px] font-bold px-2 py-1 rounded-md bg-[#C8000F]/12 text-[#C8000F]" data-testid="summary-behind">{summary.behind} behind</span>}
            {tracker?.season?.rodeos_remaining != null && <span className="text-[11px] text-slate-400 ml-auto">{tracker.season.rodeos_remaining} rodeo{tracker.season.rodeos_remaining === 1 ? "" : "s"} left this season</span>}
          </div>
        )}

        {/* Earnings trajectory chart */}
        {hasName && tracker?.earnings_target ? <EarningsTrajectory m={m} data={tracker} /> : null}

        {/* Milestones — auto */}
        {hasName && milestones && (milestones.rodeos_scored > 0) && (
          <div>
            <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>This season · auto-tracked</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MilestoneStat m={m} icon={Flame} label="Money streak" value={milestones.current_streak} color="#C8000F" />
              <MilestoneStat m={m} icon={CheckCircle2} label="Cheques won" value={milestones.cheques} color="#3B6B1E" />
              <MilestoneStat m={m} icon={MapPin} label="Rodeos in the money" value={milestones.money_rodeos} color="#003087" />
              <MilestoneStat m={m} icon={Banknote} label="Best payday" value={money(milestones.best_payday)} color="#B8860B" />
            </div>
          </div>
        )}

        {/* Auto CFR race */}
        {hasName && cfrEvents.length > 0 && (
          <div>
            <div className={`flex items-center justify-between mb-2`}>
              <span className={`text-[11px] font-bold uppercase tracking-wide ${m ? "text-slate-500" : "text-slate-400"}`}>Your CFR race · auto-tracked</span>
              <button onClick={askCoach} disabled={suggesting} data-testid="coach-suggest-btn" className="flex items-center gap-1.5 text-xs font-bold text-[#C8000F] disabled:opacity-50">
                {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {suggesting ? "Coach is thinking…" : "Ask coach for goals"}
              </button>
            </div>
            <div className="space-y-2">{cfrEvents.map((ev) => <AutoCfrCard key={ev.event} m={m} ev={ev} />)}</div>
          </div>
        )}

        {hasName && <div className={`text-[11px] font-bold uppercase tracking-wide pt-1 ${m ? "text-slate-500" : "text-slate-400"}`}>Your goals</div>}
        {goals === null ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : goals.length === 0 ? (
          <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
            <TrendingUp className="w-8 h-8 mx-auto mb-3 text-[#C8000F]" />
            <div className={`font-display font-bold ${title}`}>Set your season goals</div>
            <p className="text-sm text-slate-400 mt-1 mb-4">Track your earnings, chase a CFR spot, or add your own goals. Progress updates from your real standings.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => add({ type: "earnings", title: "Season earnings", target: 50000 })} data-testid="goal-starter-earnings" className="text-sm font-semibold px-3 py-2 rounded-lg bg-[#3B6B1E]/10 text-[#3B6B1E]">+ $50k earnings goal</button>
              <button onClick={() => setAdding(true)} data-testid="goal-starter-custom" className="text-sm font-semibold px-3 py-2 rounded-lg bg-[#C8000F]/10 text-[#C8000F]">+ Custom goal</button>
            </div>
          </div>
        ) : goals.map((g) => <GoalCard key={g.id} m={m} goal={g} onPatch={patch} onDelete={del} />)}
      </div>

      <AddGoalDialog open={adding} onClose={() => setAdding(false)} onAdd={add} plan={plan} m={m} />
      <CoachSuggestDialog suggestion={suggestion} m={m} onClose={() => setSuggestion(null)} onAdd={add} />
    </div>
  );
}

function AddGoalDialog({ open, onClose, onAdd, plan, m }) {
  const [type, setType] = useState("earnings");
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("");
  const [unit, setUnit] = useState("");
  const [event, setEvent] = useState("");
  const [deadline, setDeadline] = useState("");
  const events = plan?.events || [];

  const submit = () => {
    if (type === "earnings") return onAdd({ type, title: title || "Season earnings", target: parseFloat(target) || 0 });
    if (type === "cfr") {
      const ev = event || events[0]?.event;
      if (!ev) return toast.error("Pick an event");
      return onAdd({ type, title: title || `Make the CFR — ${ev}`, event: ev, target: 12 });
    }
    if (!title.trim()) return toast.error("Give your goal a title");
    onAdd({ type: "custom", title: title.trim(), target: parseFloat(target) || null, current: 0, unit: unit || null, deadline: deadline || null });
  };

  const inp = m ? "bg-white/5 border-white/10 text-slate-100" : "bg-white border-[#D7DBE0] text-[#14181F]";
  const DLG = m ? "bg-[#12161C] text-slate-100 border-white/10" : "bg-white text-[#14181F] border-[#E4E7EB]";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="goal-add-dialog">
        <DialogHeader><DialogTitle className="font-display">New goal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(TYPES).map(([k, v]) => {
              const Icon = v.icon;
              return (
                <button key={k} onClick={() => setType(k)} data-testid={`goal-type-${k}`}
                  className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border text-[11px] font-semibold ${type === k ? "border-[#C8000F] text-[#C8000F]" : m ? "border-white/10 text-slate-300" : "border-[#E4E7EB] text-slate-600"}`}>
                  <Icon className="w-4 h-4" /> {v.label.split(" ")[0]}
                </button>
              );
            })}
          </div>
          {type === "earnings" && (
            <label className="block text-sm">Target earnings ($)
              <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="50000" data-testid="goal-input-target" className={`w-full mt-1 rounded-lg border px-3 py-2.5 ${inp}`} />
            </label>
          )}
          {type === "cfr" && (
            <label className="block text-sm">Event
              <select value={event} onChange={(e) => setEvent(e.target.value)} data-testid="goal-input-event" className={`w-full mt-1 rounded-lg border px-3 py-2.5 ${inp}`}>
                {events.length === 0 && <option value="">No standings — set your name in Settings</option>}
                {events.map((e) => <option key={e.event} value={e.event} className={m ? "bg-[#12161C]" : ""}>{e.event} (currently #{e.rank})</option>)}
              </select>
            </label>
          )}
          {type === "custom" && (
            <>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Win Ponoka Stampede" data-testid="goal-input-title" className={`w-full rounded-lg border px-3 py-2.5 text-sm ${inp}`} />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Target # (optional)" data-testid="goal-input-ctarget" className={`rounded-lg border px-3 py-2.5 text-sm ${inp}`} />
                <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit (e.g. wins)" data-testid="goal-input-unit" className={`rounded-lg border px-3 py-2.5 text-sm ${inp}`} />
              </div>
              <label className="block text-sm">Target date (optional)
                <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="goal-input-deadline" className={`w-full mt-1 rounded-lg border px-3 py-2.5 ${inp}`} />
              </label>
            </>
          )}
          {type !== "custom" && (
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Custom title (optional)" data-testid="goal-input-optitle" className={`w-full rounded-lg border px-3 py-2.5 text-sm ${inp}`} />
          )}
          <button onClick={submit} data-testid="goal-save-btn" className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg">
            <Check className="w-4 h-4" /> Add goal
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
